import React, { useMemo, memo, useState } from 'react';
import { MemoizedChartRenderer } from '../MemoizedChartRenderer.js';
import CragStatsChart from './CragStatsChart.js';
import { CragStatsControls } from './CragStatsControls.js';
import { ClimbingPartnersControls } from './ClimbingPartnersControls.js';
import ClimbingPartnersChart from './ClimbingPartnersChart.js';
import { FlashProbabilityControls } from './FlashProbabilityControls.js';
import FlashProbabilityChart from './FlashProbabilityChart.js';
import GradeDistributionChart from './GradeDistributionChart.js';
import RoutesPerYearChart from './RoutesPerYearChart.js';
import RoutesPerYearControls from './RoutesPerYearControls.js';
import AscentProgressionChart from './AscentProgressionChart.js';
import BestClimbsChart from './BestClimbsChart.js';
import HeatmapChart from './HeatmapChart.js';
import { HeatmapControls } from './HeatmapControls.js';
import { formatGrade } from '@utils/gradeUtils.js';

// Extra options components for specific charts
const GradeDistributionOptions = ({ showDistinctRoutes, setShowDistinctRoutes, isHorizontal, onLayoutChange }) => (
  <div className="flex items-center gap-4">
    <label className="inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={showDistinctRoutes}
        onChange={(e) => setShowDistinctRoutes(e.target.checked)}
      />
      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      <span className="ml-3 text-sm font-medium text-gray-900">
        Show distinct routes
      </span>
    </label>

    <label className="inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={isHorizontal}
        onChange={(e) => onLayoutChange(e.target.checked)}
      />
      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      <span className="ml-3 text-sm font-medium text-gray-900">
        Horizontal layout
      </span>
    </label>
  </div>
);

const FlashProbabilityOptions = ({ useLogOdds, setUseLogOdds }) => (
  <FlashProbabilityControls
    useLogOdds={useLogOdds}
    setUseLogOdds={setUseLogOdds}
  />
);

export const ChartBox = memo(({
  position,
  selectedMetric,
  onChartChange,
  metrics,
  filteredData,
  climbingStyle,
  gradeSystem,
  onGradeClick,
  chartLayouts,
  onLayoutChange
}) => {
  // Existing state
  const [showDistinctRoutes, setShowDistinctRoutes] = useState(false);
  const [selectedStat, setSelectedStat] = useState('cleanAscents');
  const [numCrags, setNumCrags] = useState(30);
  // New state for flash probability
  const [useLogOdds, setUseLogOdds] = useState(false);
  // Heatmap state
  const [heatmapType, setHeatmapType] = useState('clean');
  const [timePeriod, setTimePeriod] = useState('quarterly');

  const renderExtraOptions = () => {
    switch (selectedMetric) {
      case 'partners':
        return (
          <ClimbingPartnersControls
            numPartners={numCrags}
            setNumPartners={setNumCrags}
            selectedStat={selectedStat}
            setSelectedStat={setSelectedStat}
          />
        );
      case 'grades':
        return (
          <GradeDistributionOptions 
            showDistinctRoutes={showDistinctRoutes}
            setShowDistinctRoutes={setShowDistinctRoutes}
            isHorizontal={chartLayouts[selectedMetric] ?? true}
            onLayoutChange={(isHorizontal) => onLayoutChange(selectedMetric, isHorizontal)}
          />
        );
      case 'routesPerYear':
        return (
          <RoutesPerYearControls 
            selectedStat={selectedStat}
            setSelectedStat={setSelectedStat}
          />
        );
      case 'crags':
        return (
          <CragStatsControls
            numCrags={numCrags}
            setNumCrags={setNumCrags}
            selectedStat={selectedStat}
            setSelectedStat={setSelectedStat}
          />
        );
      case 'flash':
        return (
          <FlashProbabilityControls
            useLogOdds={useLogOdds}
            setUseLogOdds={setUseLogOdds}
          />
        );
      case 'heatmap':
        return (
          <HeatmapControls
            heatmapType={heatmapType}
            setHeatmapType={setHeatmapType}
            timePeriod={timePeriod}
            setTimePeriod={setTimePeriod}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-lg border border-gray-200">
      <div className="p-2 bg-gray-50 border-b flex items-center gap-4">
        <select
          value={selectedMetric}
          onChange={(e) => onChartChange(position, e.target.value)}
          className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {metrics.map(([metric, label]) => (
            <option key={metric} value={metric}>
              {label}
            </option>
          ))}
        </select>
        {renderExtraOptions()}
      </div>
      <div className="p-2" style={{ height: '400px' }}>
        {selectedMetric === 'partners' ? (
          <ClimbingPartnersChart
            data={filteredData}
            climbingStyle={climbingStyle}
            selectedStat={selectedStat}
            numPartners={numCrags}
          />
        ) : selectedMetric === 'crags' ? (
          <CragStatsChart
            data={filteredData}
            climbingStyle={climbingStyle}
            selectedStat={selectedStat}
            numCrags={numCrags}
          />
        ) : selectedMetric === 'grades' ? (
          <GradeDistributionChart 
            data={filteredData}
            climbingStyle={climbingStyle}
            gradeSystem={gradeSystem}
            formatGradeWithStyle={formatGrade}
            onGradeClick={onGradeClick}
            isHorizontal={chartLayouts[selectedMetric]}
            showDistinctRoutes={showDistinctRoutes}
          />
        ) : selectedMetric === 'routesPerYear' ? (
          <RoutesPerYearChart
            data={filteredData}
            climbingStyle={climbingStyle}
            selectedStat={selectedStat}
          />
        ) : selectedMetric === 'flash' ? (
          <FlashProbabilityChart
            data={filteredData}
            climbingStyle={climbingStyle}
            formatGradeWithStyle={formatGrade}
            yearMode="all"
            useLogOdds={useLogOdds}
            gradeSystem={gradeSystem}
          />
        ) : selectedMetric === 'ascents' ? (
          <AscentProgressionChart
            data={filteredData}
            getGradeLabel={formatGrade}
            climbingStyle={climbingStyle}
            gradeSystem={gradeSystem}
          />
        ) : selectedMetric === 'heatmap' ? (
          <HeatmapChart
            data={filteredData}
            climbingStyle={climbingStyle}
            gradeSystem={gradeSystem}
            heatmapType={heatmapType}
            timePeriod={timePeriod}
          />
        ) : (
          <MemoizedChartRenderer
            metric={selectedMetric}
            filteredData={filteredData}
            climbingStyle={climbingStyle}
            gradeSystem={gradeSystem}
            onGradeClick={onGradeClick}
            isHorizontal={chartLayouts[selectedMetric]}
            onLayoutChange={(isHorizontal) => onLayoutChange(selectedMetric, isHorizontal)}
            showDistinctRoutes={showDistinctRoutes}
            selectedStat={selectedStat}
          />
        )}
      </div>
    </div>
  );
});

ChartBox.displayName = 'ChartBox';